// src/app/timesheets/components/admin-timesheet/admin-timesheet.component.ts

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Observable, Subject, firstValueFrom, from, mergeMap, takeUntil, toArray } from 'rxjs';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isoWeek from 'dayjs/plugin/isoWeek';

import { AdminTimesheetService } from '../../services/admin-timesheet.service';
import {
  AdminWonRowVm,
  ManualClockLogCreateItem,
  PunchEventVm,
  UserListItem,
  UserWonOption,
  WeeklyReportAssignedSchedule,
  WeeklyReportBookedHourItem,
  WeeklyReportBookedHoursRow,
  WeeklyReportDayLog,
  WeeklyReportDto,
  WeeklyReportTimeLogsRow,
  WeeklyReportUserScheduleRow,
} from '../../models/admin-timesheet.model';
import { FilterService } from '../../../services/filter.service';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);

type ToastKind = 'success' | 'error' | 'info';

type RowSnapshot = Array<{
  rowId: string;
  workOrderNumberId: number | null;
  workOrderNumberLabel: string;
  payCodeId: number | null;
  payCodeName: string;
  dailyBooked: number[];
  dailyBookedItems: WeeklyReportBookedHourItem[][];
  rowTotal: number;
  isNew?: boolean;
  pendingDelete?: boolean;
}>;

type AdminRow = AdminWonRowVm & { pendingDelete?: boolean };

type ApprovalScope = 'day' | 'week';
type ApprovalAction = 'approve' | 'change';

interface ApprovalItemVm {
  bookedHourId: number;
  dayIndex: number;
  date: string;
  dayLabel: string;
  won: string;
  payCode: string;
  currentHours: number;
  newHours: number;
  supervisorApprovedDate: string | null;
  approvedBySupervisorName: string;
}

interface PayableHoursSyncLockVm {
  id: number;
  yywwId?: number | null;
  createdAt?: string | null;
  createdBy?: {
    id?: number | null;
    firstName?: string | null;
    lastName?: string | null;
    employeeNumber?: string | null;
  } | null;
}

@Component({
  selector: 'app-admin-timesheet',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './admin-timesheet.component.html',
  styleUrls: ['./admin-timesheet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminTimesheetComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  isLoading = true;
  isSaving = false;
  isExportingTimesheet = false;
  errorMessage: string | null = null;

  toastMessage: string | null = null;
  toastKind: ToastKind = 'info';
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  userId!: number;
  yyyyww!: string;

  employee: UserListItem | null = null;
  weekDates: string[] = [];

  userWonOptions: UserWonOption[] = [];

  report: WeeklyReportDto | null = null;
  payrollLock: PayableHoursSyncLockVm | null = null;
  employeeTimeLogs: WeeklyReportTimeLogsRow | null = null;
  employeeBooked: WeeklyReportBookedHoursRow | null = null;
  employeeSchedule: WeeklyReportUserScheduleRow | null = null;

  rows: AdminRow[] = [];

  returnWeek: string | null = null;
  returnTo: string | null = null;
  returnUrl: string | null = null;

  dailyClocked: number[] = Array(7).fill(0);
  dailyScheduled: (WeeklyReportAssignedSchedule | null)[] = Array(7).fill(null);
  dailyBookedTotals: number[] = Array(7).fill(0);

  weeklyClockedTotal = 0;
  weeklyBookedTotal = 0;
  weeklyScheduledTotal = 0;

  payCodeName = '';
  formattedPunches: string[] = Array(7).fill('');

  selectedDayIndex = 0;
  punchEvents: PunchEventVm[] = [];
  selectedDayTotalClockedHours = 0;

  isPunchActionBusy = false;
  punchActionError: string | null = null;

  isAddPunchOpen = false;
  addPunchDate = '';
  addPunchTime = '';
  addPunchTimeZoneId = dayjs.tz.guess();

  approvalModalOpen = false;
  approvalScope: ApprovalScope = 'day';
  approvalAction: ApprovalAction = 'approve';
  approvalDayIndex: number | null = null;
  approvalRows: ApprovalItemVm[] = [];
  approvalBypassEmployeeApproval = false;
  saveBypassEmployeeApproval = false;
  approvalError: string | null = null;
  isApprovalBusy = false;

  isDirty = false;
  private initialSnapshotJson = '';
  private initialRowsSnapshot: RowSnapshot = [];
  filterService = inject(FilterService);

  private usersLoaded = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly svc: AdminTimesheetService,
    private readonly cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.userId = Number(params.get('userId'));
      this.yyyyww = String(params.get('yyyyww') || '');
      const queryParams = this.route.snapshot.queryParamMap;
      this.returnWeek = queryParams.get('returnWeek');
      this.returnTo = queryParams.get('returnTo');
      this.returnUrl = this.normalizeReturnUrl(queryParams.get('returnUrl'));

      if (!Number.isFinite(this.userId) || this.userId <= 0 || !this.isValidYyyyww(this.yyyyww)) {
        this.errorMessage = 'Invalid employee or week';
        this.isLoading = false;
        this.refreshView();
        return;
      }
      void this.load();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearToast();

    document.body.style.overflow = '';
  }

  private refreshView(): void {
    this.cd.detectChanges();
  }

  showToast(kind: ToastKind, message: string, ms = 3200): void {
    this.toastKind = kind;
    this.toastMessage = message;

    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }

    this.toastTimer = setTimeout(() => {
      this.toastMessage = null;
      this.toastTimer = null;
      this.cd.markForCheck();
    }, ms);

    this.cd.markForCheck();
  }

  clearToast(): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }

    this.toastMessage = null;
    this.cd.markForCheck();
  }

  get currentWeekLabel(): string {
    const dates = this.buildWeekDatesFromYyyyww(this.yyyyww);
    if (!dates.length) return this.yyyyww;

    const start = dayjs(dates[0]).format('MMM D');
    const end = dayjs(dates[6]).format('MMM D, YYYY');
    return `${this.yyyyww} (${start} - ${end})`;
  }

  get payableHoursSync(): PayableHoursSyncLockVm | null {
    return this.payrollLock;
  }

  get isPayrollSynced(): boolean {
    return !!this.payrollLock?.id;
  }

  get isTimesheetLocked(): boolean {
    return this.isPayrollSynced;
  }

  get canEditTimesheet(): boolean {
    return !this.isPayrollSynced;
  }

  get canEditBookedHours(): boolean {
    return !this.isPayrollSynced;
  }

  get canAddPunches(): boolean {
    return !this.isPayrollSynced && !!this.employee?.rfid;
  }

  get canEditPunches(): boolean {
    return !this.isPayrollSynced;
  }

  get canApproveBookedHours(): boolean {
    return !this.isPayrollSynced;
  }

  private extractPayableHoursSync(report: unknown): PayableHoursSyncLockVm | null {
    const direct = (report as { payableHoursSync?: PayableHoursSyncLockVm | null } | null)
      ?.payableHoursSync;

    if (direct?.id) {
      return direct;
    }

    const wrapped = (report as { data?: { payableHoursSync?: PayableHoursSyncLockVm | null } } | null)
      ?.data?.payableHoursSync;

    return wrapped?.id ? wrapped : null;
  }

  get payrollSyncCreatedByLabel(): string {
    const createdBy = this.payableHoursSync?.createdBy;

    if (!createdBy) {
      return 'Unknown user';
    }

    const fullName = `${createdBy.firstName ?? ''} ${createdBy.lastName ?? ''}`.trim();

    return fullName || createdBy.employeeNumber || 'Unknown user';
  }

  get payrollSyncCreatedAtLabel(): string {
    const createdAt = this.payableHoursSync?.createdAt;

    if (!createdAt) {
      return 'Unknown time';
    }

    return dayjs(createdAt).format('MMM D, YYYY h:mm A');
  }

  get payrollLockedMessage(): string {
    if (!this.isPayrollSynced) {
      return '';
    }

    return `Payroll sync has already been run for this week by ${this.payrollSyncCreatedByLabel} on ${this.payrollSyncCreatedAtLabel}. This timesheet is locked.`;
  }

  private blockIfPayrollSynced(actionLabel = 'This action'): boolean {
    if (!this.isPayrollSynced) {
      return false;
    }

    this.showToast('info', `${actionLabel} is locked because payroll sync has already been run for this week.`);
    return true;
  }

  isBookedHoursInputDisabled(row: AdminWonRowVm): boolean {
    return this.isPayrollSynced || (!!row.isNew && (!row.workOrderNumberId || !row.payCodeId));
  }

  isWonSelectDisabled(row: AdminWonRowVm): boolean {
    return this.isPayrollSynced || !row.isNew || !this.userWonOptions.length;
  }

  isPayCodeSelectDisabled(row: AdminWonRowVm): boolean {
    return this.isPayrollSynced || !row.isNew || !this.filterService.filterValues?.paycodes?.length;
  }

  getLockedTitle(actionLabel = 'This action'): string {
    return this.isPayrollSynced
      ? `${actionLabel} is locked because payroll sync has already been run for this week.`
      : '';
  }

  goBackToOverview(): void {
    if (this.returnUrl) {
      void this.router.navigateByUrl(this.returnUrl);
      return;
    }

    const week = this.returnWeek || this.yyyyww;

    if (this.isOverviewReturnSource()) {
      void this.router.navigate(['/timesheets/admin-overview'], {
        queryParams: { week },
      });
      return;
    }

    void this.router.navigate(['/timesheets'], {
      queryParams: { week },
    });
  }

  private isOverviewReturnSource(): boolean {
    const source = String(this.returnTo ?? '').trim().toLowerCase();

    return (
      source === 'overview' ||
      source === 'admin-overview' ||
      source === 'three-week' ||
      source === 'three-week-overview'
    );
  }

  private normalizeReturnUrl(value: string | null): string | null {
    const url = String(value ?? '').trim();

    if (!url || !url.startsWith('/timesheets')) {
      return null;
    }

    if (url.startsWith('/timesheets/admin/')) {
      return null;
    }

    return url;
  }

  private buildAdminNavigationQueryParams(): Record<string, string> {
    const queryParams: Record<string, string> = {};

    if (this.returnWeek) {
      queryParams['returnWeek'] = this.returnWeek;
    }

    if (this.returnTo) {
      queryParams['returnTo'] = this.returnTo;
    }

    if (this.returnUrl) {
      queryParams['returnUrl'] = this.returnUrl;
    }

    return queryParams;
  }

  prevWeek(): void {
    void this.navigateToWeek(this.addWeeks(this.yyyyww, -1));
  }

  nextWeek(): void {
    void this.navigateToWeek(this.addWeeks(this.yyyyww, 1));
  }

  private async navigateToWeek(nextWeek: string): Promise<void> {
    if (!this.userId || !this.isValidYyyyww(nextWeek)) return;

    await this.router.navigate(['/timesheets/admin', this.userId, nextWeek], {
      queryParams: this.buildAdminNavigationQueryParams(),
    });
  }

  private getLastWeekMondayCutoffYmd(): string {
    const tz = dayjs.tz.guess();
    return dayjs().tz(tz).startOf('isoWeek').subtract(1, 'week').format('YYYY-MM-DD');
  }

  private isPunchEditDateAllowed(ymd: string | null | undefined): boolean {
    if (!ymd) return false;
    return ymd >= this.getLastWeekMondayCutoffYmd();
  }

  get isSelectedPunchDayEditable(): boolean {
    const selectedDate = this.getSelectedDate();
    return !this.isPayrollSynced && this.isPunchEditDateAllowed(selectedDate);
  }

  private explainPunchCutoff(): string {
    return `Punch edits are only allowed on/after ${this.getLastWeekMondayCutoffYmd()}.`;
  }

  async load(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = null;
    this.punchActionError = null;
    this.refreshView();

    try {
      const basePromises: Array<Promise<unknown>> = [
        firstValueFrom(this.svc.getWeeklyReport(this.yyyyww, this.userId)),
      ];

      if (!this.usersLoaded) {
        basePromises.push(firstValueFrom(this.svc.getAllUsers()));
      }

      const results = await Promise.all(basePromises);

      const report = results[0] as WeeklyReportDto;
      const users = (results[1] as UserListItem[] | undefined) ?? null;
      this.report = report;
      this.payrollLock = this.extractPayableHoursSync(report);

      if (users) {
        this.employee = users.find((user) => user.id === this.userId) ?? this.employee;
        this.usersLoaded = true;
      }

      const reportUser =
        report.timeLogs[0]?.user ??
        report.bookedhours[0]?.user ??
        report.userSchedules[0]?.user ??
        report.invalidUserClockLogs[0]?.user ??
        report.dayOffRequests[0]?.user ??
        null;

      if (!this.employee && reportUser) {
        this.employee = {
          id: reportUser.id,
          firstName: reportUser.firstName,
          lastName: reportUser.lastName,
          employeeNumber: reportUser.employeeNumber ?? '',
          rfid: reportUser.rfid ?? null,
          department: null,
          shift: null,
        };
      }

      if (!this.employee || this.employee.id !== this.userId) {
        throw new Error(`Employee ${this.userId} not found`);
      }

      this.userWonOptions = await firstValueFrom(
        this.svc.getAllUserWonsAsOptions(this.employee.id),
      );

      this.employeeTimeLogs = report.timeLogs[0] ?? null;
      this.employeeBooked = report.bookedhours[0] ?? null;
      this.employeeSchedule = report.userSchedules[0] ?? null;

      this.weekDates = this.deriveWeekDates(
        this.employeeTimeLogs,
        this.employeeBooked,
        this.employeeSchedule,
      );

      this.computeClocked();
      this.computePunchTooltips();
      this.computeScheduled();

      this.rows = this.buildWonRowsFromBooked();
      this.recomputeAllTotals();
      this.captureSnapshot();

      if (this.isPayrollSynced) {
        this.isDirty = false;
        this.isSaving = false;
        this.isPunchActionBusy = false;
        this.isApprovalBusy = false;
        this.isAddPunchOpen = false;
        this.approvalModalOpen = false;
        this.approvalRows = [];
      }

      this.initPunchDaySelection();
      this.rebuildPunchesForSelectedDay();
    } catch {
      this.errorMessage = 'Failed to load admin timesheet';
      this.showToast('error', 'Failed to load admin timesheet');
    } finally {
      this.isLoading = false;
      this.refreshView();
    }
  }

  async exportTimesheetToExcel(): Promise<void> {
    if (this.isExportingTimesheet) {
      return;
    }

    if (!this.employee || !this.weekDates.length) {
      this.showToast('info', 'No timesheet data available to export.');
      return;
    }

    this.isExportingTimesheet = true;
    this.cd.markForCheck();

    try {
      const XLSX = await import('xlsx');

      const employeeName = this.getEmployeeDisplayName();
      const safeEmployeeName = this.sanitizeFileName(employeeName);
      const fileName = `timesheet_${this.yyyyww}_${safeEmployeeName}_${dayjs().format('YYYY-MM-DD')}.xlsx`;

      const workbook = XLSX.utils.book_new();

      const summarySheet = XLSX.utils.aoa_to_sheet([
        ['Employee Timesheet Export'],
        [],
        ['Employee', employeeName],
        ['Employee #', this.employee.employeeNumber ?? ''],
        ['Week', this.yyyyww],
        ['Week Range', this.currentWeekLabel],
        ['Payroll Locked', this.isPayrollSynced ? 'Yes' : 'No'],
        ['Weekly Scheduled Total', this.round2(this.weeklyScheduledTotal)],
        ['Weekly Clocked Total', this.round2(this.weeklyClockedTotal)],
        ['Weekly Booked Total', this.round2(this.weeklyBookedTotal)],
        ['Exported At', dayjs().format('YYYY-MM-DD h:mm A')],
      ]);

      const dailyTotalsSheet = XLSX.utils.json_to_sheet(
        this.weekDates.map((date, dayIndex) => {
          const scheduled = this.round2(this.getDailyScheduledHours(dayIndex));
          const clocked = this.round2(this.dailyClocked[dayIndex] ?? 0);
          const booked = this.round2(this.dailyBookedTotals[dayIndex] ?? 0);

          return {
            Day: this.getDayLabel(dayIndex),
            Date: date,
            'Schedule Time': this.getScheduledTimeRange(this.dailyScheduled[dayIndex]),
            Scheduled: scheduled,
            Clocked: clocked,
            Booked: booked,
            'Booked - Clocked': this.round2(booked - clocked),
            'Booked - Scheduled': this.round2(booked - scheduled),
            Status: this.getDailyExportStatus(dayIndex),
          };
        }),
      );

      const bookedHoursSheet = XLSX.utils.json_to_sheet(
        this.rows
          .filter((row) => !row.pendingDelete)
          .map((row) => ({
            WON: row.workOrderNumberLabel || '',
            'Pay Code': row.payCodeName || '',
            Monday: this.round2(row.dailyBooked[0] ?? 0),
            Tuesday: this.round2(row.dailyBooked[1] ?? 0),
            Wednesday: this.round2(row.dailyBooked[2] ?? 0),
            Thursday: this.round2(row.dailyBooked[3] ?? 0),
            Friday: this.round2(row.dailyBooked[4] ?? 0),
            Saturday: this.round2(row.dailyBooked[5] ?? 0),
            Sunday: this.round2(row.dailyBooked[6] ?? 0),
            Total: this.round2(row.rowTotal ?? 0),
          })),
      );

      const punchRows = this.weekDates.flatMap((date, dayIndex) => {
        const dayLog = this.getDayLogByDate(date);
        const punches = this.svc.buildPunchEventsForDay(dayLog) ?? [];

        if (!punches.length) {
          return [
            {
              Day: this.getDayLabel(dayIndex),
              Date: date,
              Type: '',
              Time: '',
              Status: 'No punches',
              'Updated By': '',
              'Updated At': '',
            },
          ];
        }

        return punches.map((punch) => ({
          Day: this.getDayLabel(dayIndex),
          Date: date,
          Type: this.getPunchExportType(punch),
          Time: this.getPunchExportTime(punch),
          Status: punch.isInvalid ? 'Invalid' : 'Valid',
          'Updated By': this.getPunchUpdatedByLabel(punch),
          'Updated At': this.getPunchUpdatedAtLabel(punch),
        }));
      });

      const punchesSheet = XLSX.utils.json_to_sheet(punchRows);

      this.autoSizeWorksheetColumns(summarySheet);
      this.autoSizeWorksheetColumns(dailyTotalsSheet);
      this.autoSizeWorksheetColumns(bookedHoursSheet);
      this.autoSizeWorksheetColumns(punchesSheet);

      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
      XLSX.utils.book_append_sheet(workbook, dailyTotalsSheet, 'Daily Totals');
      XLSX.utils.book_append_sheet(workbook, bookedHoursSheet, 'Booked Hours');
      XLSX.utils.book_append_sheet(workbook, punchesSheet, 'Punches');

      XLSX.writeFile(workbook, fileName);

      this.showToast('success', 'Timesheet exported successfully.');
    } catch (error) {
      console.error('Timesheet export failed:', error);
      this.showToast('error', 'Failed to export timesheet.');
    } finally {
      this.isExportingTimesheet = false;
      this.cd.markForCheck();
    }
  }

  private getEmployeeDisplayName(): string {
    if (!this.employee) {
      return 'Employee';
    }

    const fullName = `${this.employee.firstName ?? ''} ${this.employee.lastName ?? ''}`.trim();

    return fullName || this.employee.employeeNumber || 'Employee';
  }

  private sanitizeFileName(value: string): string {
    return String(value || 'employee')
      .trim()
      .replace(/[^a-z0-9]+/gi, '_')
      .replace(/^_+|_+$/g, '')
      .toLowerCase();
  }

  private getDailyScheduledHours(dayIndex: number): number {
    const schedule = this.dailyScheduled[dayIndex];

    if (!schedule) {
      return 0;
    }

    return this.computeDurationHours(schedule);
  }

  private getDailyExportStatus(dayIndex: number): string {
    if (this.isDayFullySupervisorApproved(dayIndex)) {
      return 'Approved';
    }

    const payCodeClass = this.getDailyOverridePayCodeClass(dayIndex);

    if (payCodeClass === 'paycode-holiday') {
      return 'Holiday';
    }

    if (payCodeClass === 'requested-offtime') {
      return 'Requested Off Time';
    }

    const booked = Number(this.dailyBookedTotals[dayIndex] ?? 0);
    const clocked = Number(this.dailyClocked[dayIndex] ?? 0);

    if (this.isMatch(booked, clocked) && (booked > 0 || clocked > 0)) {
      return 'Match';
    }

    if (booked > clocked) {
      return 'Booked Over Clocked';
    }

    if (booked < clocked) {
      return 'Booked Under Clocked';
    }

    return '';
  }

  private getDayLogByDate(date: string): WeeklyReportDayLog | null {
    if (!date || !this.employeeTimeLogs) {
      return null;
    }

    const matchingDays = this.employeeTimeLogs.logs?.filter((day) => day.date === date) ?? [];

    return {
      date,
      timeLogs: matchingDays.flatMap((day) => day.timeLogs ?? []),
      invalidUserClockLogs: Array.from(
        new Map(
          matchingDays
            .flatMap((day) => day.invalidUserClockLogs ?? [])
            .map((log) => [log.id, log]),
        ).values(),
      ),
    };
  }

  private getPunchExportType(punch: PunchEventVm): string {
    const rawPunch = punch as unknown as {
      type?: string | null;
      eventType?: string | null;
      direction?: string | null;
      punchType?: string | null;
    };

    return String(
      rawPunch.type ??
      rawPunch.eventType ??
      rawPunch.direction ??
      rawPunch.punchType ??
      'Punch',
    );
  }

  private getPunchExportTime(punch: PunchEventVm): string {
    const rawPunch = punch as unknown as {
      displayTime?: string | null;
      time?: string | null;
      localTime?: string | null;
      logTime?: string | null;
      logTimeUtc?: string | null;
    };

    if (rawPunch.displayTime) {
      return rawPunch.displayTime;
    }

    if (rawPunch.time) {
      return rawPunch.time;
    }

    if (rawPunch.localTime) {
      return rawPunch.localTime;
    }

    if (rawPunch.logTime) {
      return this.formatTime(rawPunch.logTime);
    }

    if (rawPunch.logTimeUtc) {
      return this.formatTime(rawPunch.logTimeUtc);
    }

    return '';
  }

  private autoSizeWorksheetColumns(sheet: unknown): void {
    const worksheet = sheet as {
      '!ref'?: string;
      '!cols'?: Array<{ wch: number }>;
      [cellAddress: string]: unknown;
    };

    const range = worksheet['!ref'];

    if (!range) {
      return;
    }

    const XLSXRange = this.decodeWorksheetRange(range);
    const columns: Array<{ wch: number }> = [];

    for (let columnIndex = XLSXRange.startColumn; columnIndex <= XLSXRange.endColumn; columnIndex += 1) {
      let maxLength = 10;

      for (let rowIndex = XLSXRange.startRow; rowIndex <= XLSXRange.endRow; rowIndex += 1) {
        const cellAddress = this.encodeWorksheetCell(rowIndex, columnIndex);
        const cell = worksheet[cellAddress] as { v?: unknown } | undefined;
        const value = cell?.v === null || cell?.v === undefined ? '' : String(cell.v);

        maxLength = Math.max(maxLength, value.length + 2);
      }

      columns.push({ wch: Math.min(maxLength, 45) });
    }

    worksheet['!cols'] = columns;
  }

  private decodeWorksheetRange(range: string): {
    startRow: number;
    endRow: number;
    startColumn: number;
    endColumn: number;
  } {
    const [start, end] = range.split(':');

    const startCell = this.decodeWorksheetCell(start);
    const endCell = this.decodeWorksheetCell(end ?? start);

    return {
      startRow: startCell.row,
      endRow: endCell.row,
      startColumn: startCell.column,
      endColumn: endCell.column,
    };
  }

  private decodeWorksheetCell(cell: string | undefined): { row: number; column: number } {
    const match = String(cell ?? 'A1').match(/^([A-Z]+)(\d+)$/i);

    if (!match) {
      return { row: 0, column: 0 };
    }

    const letters = match[1].toUpperCase();
    const row = Number(match[2]) - 1;

    let column = 0;

    for (let index = 0; index < letters.length; index += 1) {
      column = column * 26 + (letters.charCodeAt(index) - 64);
    }

    return {
      row,
      column: column - 1,
    };
  }

  private encodeWorksheetCell(row: number, column: number): string {
    let columnNumber = column + 1;
    let letters = '';

    while (columnNumber > 0) {
      const remainder = (columnNumber - 1) % 26;
      letters = String.fromCharCode(65 + remainder) + letters;
      columnNumber = Math.floor((columnNumber - 1) / 26);
    }

    return `${letters}${row + 1}`;
  }

  private async refreshWeeklyReportOnly(preserveSelectedDate = true): Promise<void> {
    if (!this.employee) return;

    const previousSelectedDate = preserveSelectedDate ? this.getSelectedDate() : null;
    const report = await firstValueFrom(this.svc.getWeeklyReport(this.yyyyww, this.userId));

    this.report = report;
    this.payrollLock = this.extractPayableHoursSync(report);
    this.employeeTimeLogs = report.timeLogs[0] ?? null;
    this.employeeBooked = report.bookedhours[0] ?? null;
    this.employeeSchedule = report.userSchedules[0] ?? null;

    this.weekDates = this.deriveWeekDates(
      this.employeeTimeLogs,
      this.employeeBooked,
      this.employeeSchedule,
    );

    this.computeClocked();
    this.computePunchTooltips();
    this.computeScheduled();

    this.rows = this.buildWonRowsFromBooked();
    this.recomputeAllTotals();
    this.captureSnapshot();

    if (this.isPayrollSynced) {
      this.isDirty = false;
      this.isSaving = false;
      this.isPunchActionBusy = false;
      this.isApprovalBusy = false;
      this.isAddPunchOpen = false;
      this.approvalModalOpen = false;
      this.approvalRows = [];
    }

    if (previousSelectedDate) {
      const index = this.weekDates.findIndex((date) => date === previousSelectedDate);
      this.selectedDayIndex = index >= 0 ? index : 0;
    } else {
      this.initPunchDaySelection();
    }

    this.rebuildPunchesForSelectedDay();
    this.cd.markForCheck();
  }

  private deriveWeekDates(
    timeLogs: WeeklyReportTimeLogsRow | null,
    booked: WeeklyReportBookedHoursRow | null,
    schedule: WeeklyReportUserScheduleRow | null,
  ): string[] {
    const logDates = timeLogs?.logs?.map((day) => day.date).filter(Boolean) ?? [];
    if (logDates.length) {
      return this.normalizeWeekDates(logDates);
    }

    const bookedDates = booked?.bookedHours?.map((day) => day.date).filter(Boolean) ?? [];
    if (bookedDates.length) {
      return this.normalizeWeekDates(bookedDates);
    }

    const scheduleDates =
      schedule?.schedules
        ?.map((item) => item.assignedDate)
        .filter((value): value is string => !!value) ?? [];

    if (scheduleDates.length) {
      return this.normalizeWeekDates(scheduleDates);
    }

    return this.buildWeekDatesFromYyyyww(this.yyyyww);
  }

  private normalizeWeekDates(dates: string[]): string[] {
    const unique = Array.from(new Set((dates ?? []).filter(Boolean))).sort();

    if (unique.length >= 7) {
      return unique.slice(0, 7);
    }

    const fallback = this.buildWeekDatesFromYyyyww(this.yyyyww);
    return fallback.length ? fallback : unique;
  }

  private buildWeekDatesFromYyyyww(yyyyww: string): string[] {
    const match = String(yyyyww ?? '').match(/^(\d{4})-?(\d{2})$/);

    if (!match) {
      return this.buildCurrentIsoWeekDates();
    }

    const isoYear = Number(match[1]);
    const isoWeekNumber = Number(match[2]);

    if (!Number.isFinite(isoYear) || !Number.isFinite(isoWeekNumber) || isoWeekNumber <= 0) {
      return this.buildCurrentIsoWeekDates();
    }

    const januaryFourth = dayjs(`${isoYear}-01-04`);
    const monday = januaryFourth.startOf('isoWeek').add(isoWeekNumber - 1, 'week');

    if (!monday.isValid()) {
      return this.buildCurrentIsoWeekDates();
    }

    return Array.from({ length: 7 }, (_, index) => monday.add(index, 'day').format('YYYY-MM-DD'));
  }

  private buildCurrentIsoWeekDates(): string[] {
    const monday = dayjs().startOf('isoWeek');
    return Array.from({ length: 7 }, (_, index) => monday.add(index, 'day').format('YYYY-MM-DD'));
  }

  private initPunchDaySelection(): void {
    const today = dayjs().format('YYYY-MM-DD');

    if (!this.weekDates.length) {
      this.weekDates = this.buildWeekDatesFromYyyyww(this.yyyyww);
    }

    const index = this.weekDates.findIndex((date) => date === today);
    this.selectedDayIndex = index >= 0 ? index : 0;

    this.addPunchTimeZoneId = dayjs.tz.guess();
    this.addPunchDate = this.getSelectedDate() ?? today;
    this.addPunchTime = '';
  }

  onSelectPunchDay(dayIndex: number): void {
    this.selectedDayIndex = Math.max(0, Math.min(6, Number(dayIndex) || 0));
    this.addPunchDate = this.getSelectedDate() ?? dayjs().format('YYYY-MM-DD');
    this.addPunchTime = '';

    this.rebuildPunchesForSelectedDay();
    this.cd.markForCheck();
  }

  private rebuildPunchesForSelectedDay(): void {
    const dayLog = this.getSelectedDayLog();

    this.punchEvents = this.svc.buildPunchEventsForDay(dayLog);
    this.selectedDayTotalClockedHours = this.svc.computeTotalClockedHoursForDay(dayLog);
    this.addPunchDate = this.getSelectedDate() ?? this.addPunchDate;

    this.cd.markForCheck();
  }

  private getSelectedDate(): string | null {
    if (!this.weekDates.length) {
      const fallback = this.buildWeekDatesFromYyyyww(this.yyyyww);
      return fallback[0] ?? null;
    }

    return this.weekDates[this.selectedDayIndex] ?? this.weekDates[0] ?? null;
  }

  private getSelectedDayLog(): WeeklyReportDayLog | null {
    const selectedDate = this.getSelectedDate();
    if (!selectedDate || !this.employeeTimeLogs) return null;

    const matchingDays =
      this.employeeTimeLogs.logs?.filter((day) => day.date === selectedDate) ?? [];

    const timeLogs = matchingDays.flatMap((day) => day.timeLogs ?? []);

    const invalidUserClockLogs = Array.from(
      new Map(
        matchingDays
          .flatMap((day) => day.invalidUserClockLogs ?? [])
          .map((log) => [log.id, log]),
      ).values(),
    );

    return {
      date: selectedDate,
      timeLogs,
      invalidUserClockLogs,
    };
  }

  openAddPunch(): void {
    if (this.blockIfPayrollSynced('Adding punches')) {
      return;
    }

    this.punchActionError = null;
    this.addPunchDate = this.getSelectedDate() ?? dayjs().format('YYYY-MM-DD');
    this.addPunchTime = '';
    this.addPunchTimeZoneId = dayjs.tz.guess();

    this.isAddPunchOpen = true;

    document.body.style.overflow = 'hidden';

    this.cd.markForCheck();
  }

  closeAddPunch(): void {
    this.isAddPunchOpen = false;

    document.body.style.overflow = '';

    this.cd.markForCheck();
  }

  async submitAddPunch(): Promise<void> {
    if (this.blockIfPayrollSynced('Adding punches')) {
      this.isAddPunchOpen = false;
      this.cd.markForCheck();
      return;
    }

    if (!this.employee?.rfid) {
      this.showToast('error', 'Employee has no RFID; cannot create manual punches.');
      return;
    }

    const date = (this.addPunchDate || '').trim();
    const time = (this.addPunchTime || '').trim();

    if (!date || !time) {
      this.showToast('info', 'Please select a date and time.');
      return;
    }

    const timeZoneId = this.addPunchTimeZoneId || dayjs.tz.guess();
    const local = dayjs.tz(`${date} ${time}`, 'YYYY-MM-DD HH:mm', timeZoneId);

    if (!local.isValid()) {
      this.showToast('error', 'Invalid date/time.');
      return;
    }

    const payload: ManualClockLogCreateItem[] = [
      {
        RFID: this.employee.rfid,
        logTime: local.utc().toISOString(),
        timeZondId: timeZoneId,
      },
    ];

    this.isPunchActionBusy = true;
    this.punchActionError = null;
    this.cd.markForCheck();

    try {
      await firstValueFrom(this.svc.createManualClockLogs(payload));

      this.isAddPunchOpen = false;
      document.body.style.overflow = '';

      this.showToast('success', 'Manual punch added.');
      await this.refreshWeeklyReportOnly(true);

    } catch {
      this.showToast('error', 'Failed to add manual punch.');
    } finally {
      this.isPunchActionBusy = false;
      this.refreshView();
    }
  }

  isPunchEventEditable(punch: PunchEventVm): boolean {
    return !this.isPayrollSynced && this.isPunchEditDateAllowed(punch?.displayDate);
  }

  async invalidatePunch(clockLogId: number, punchDateYmd?: string): Promise<void> {
    if (this.blockIfPayrollSynced('Punch edits')) {
      return;
    }

    const id = Number(clockLogId);
    if (!Number.isFinite(id) || id <= 0) return;

    const date = (punchDateYmd || this.getSelectedDate() || '').trim();
    if (!this.isPunchEditDateAllowed(date)) {
      this.showToast('info', this.explainPunchCutoff());
      return;
    }

    const confirmed = window.confirm(
      'Invalidate this punch?\n\nThis will remove it and may re-pair surrounding punches automatically.',
    );
    if (!confirmed) return;

    this.isPunchActionBusy = true;
    this.punchActionError = null;
    this.cd.markForCheck();

    try {
      await firstValueFrom(this.svc.invalidateClockLog(id));
      this.showToast('success', 'Punch invalidated.');
      await this.refreshWeeklyReportOnly(true);
    } catch {
      this.showToast('error', 'Failed to invalidate punch.');
    } finally {
      this.isPunchActionBusy = false;
      this.refreshView();
    }
  }

  getDailySummaryClass(dayIndex: number): string {
    if (this.isDayFullySupervisorApproved(dayIndex)) {
      return 'approved';
    }

    const payCodeClass = this.getDailyOverridePayCodeClass(dayIndex);
    if (payCodeClass) {
      return payCodeClass;
    }

    const booked = Number(this.dailyBookedTotals[dayIndex] ?? 0);
    const clocked = Number(this.dailyClocked[dayIndex] ?? 0);

    if (this.isMatch(booked, clocked) && (booked > 0 || clocked > 0)) {
      return 'match';
    }

    if (booked > clocked) {
      return 'over';
    }

    if (booked < clocked) {
      return 'under';
    }

    return '';
  }

  isDayFullySupervisorApproved(dayIndex: number): boolean {
    const items = this.getBookedHourItemsForDay(dayIndex);

    return items.length > 0 && items.every((item) => !!item.supervisorApprovedDate);
  }

  isWeekFullySupervisorApproved(): boolean {
    const items = this.getAllBookedHourItemsForWeek();

    return items.length > 0 && items.every((item) => !!item.supervisorApprovedDate);
  }

  hasApprovalItemsForWeek(): boolean {
    return !this.isPayrollSynced && this.getAllUnapprovedBookedHourItemsForWeek().length > 0;
  }

  private getDailyOverridePayCodeClass(dayIndex: number): string | null {
    const items = this.rows.flatMap((row) => row.dailyBookedItems?.[dayIndex] ?? []);

    const names = items
      .map((item) => String(item.payCode?.name ?? '').trim().toLowerCase())
      .filter(Boolean);

    if (!names.length) {
      return null;
    }

    if (names.some((x) => x.includes('holiday'))) {
      return 'paycode-holiday';
    }

    if (
      names.some(
        (x) =>
          x.includes('vac') ||
          x.includes('pto') ||
          x.includes('sick') ||
          x.includes('personal') ||
          x.includes('bereav') ||
          x.includes('jury') ||
          x.includes('unpaid') ||
          x.includes('off'),
      )
    ) {
      return 'requested-offtime';
    }

    return null;
  }

  addWorkOrderRow(): void {
    if (this.blockIfPayrollSynced('Adding booked hours')) {
      return;
    }

    const rowId = globalThis.crypto?.randomUUID?.() ?? `row_${Date.now()}_${Math.random()}`;

    this.rows = [
      ...this.rows,
      {
        rowId,
        workOrderNumberId: null,
        workOrderNumberLabel: '',
        payCodeId: null,
        payCodeName: '',
        dailyBooked: Array(7).fill(0),
        dailyBookedItems: Array.from({ length: 7 }, () => [] as WeeklyReportBookedHourItem[]),
        rowTotal: 0,
        isNew: true,
      } as AdminRow,
    ];

    this.recomputeAllTotals();
    this.markDirty();
    this.cd.markForCheck();
  }

  onNewRowWonChange(row: AdminWonRowVm): void {
    if (this.blockIfPayrollSynced('Editing booked hours')) {
      return;
    }

    const option = this.userWonOptions.find(
      (item) => item.workOrderNumberId === row.workOrderNumberId,
    );
    row.workOrderNumberLabel = option?.won ?? '';
    this.markDirty();
    this.cd.markForCheck();
  }

  onNewRowPayCodeChange(row: AdminWonRowVm): void {
    if (this.blockIfPayrollSynced('Editing booked hours')) {
      return;
    }

    const payCode = this.filterService.filterValues?.paycodes.find(
      (item) => item.id === row.payCodeId,
    );
    row.payCodeName = payCode?.name ?? '';
    this.markDirty();
    this.cd.markForCheck();
  }

  onBookedHoursInput(row: AdminWonRowVm, dayIndex: number, value: unknown): void {
    if (this.blockIfPayrollSynced('Editing booked hours')) {
      return;
    }

    const numericValue = Number(value);

    row.dailyBooked[dayIndex] = Number.isFinite(numericValue) ? Math.max(0, numericValue) : 0;
    row.rowTotal = this.sum(row.dailyBooked);

    this.recomputeAllTotals();
    this.markDirty();
    this.cd.markForCheck();
  }

  cancelChanges(): void {
    if (this.isPayrollSynced) {
      this.isDirty = false;
      this.saveBypassEmployeeApproval = false;
      this.captureSnapshot();
      this.showToast('info', 'Timesheet is locked because payroll sync has already been run.');
      this.cd.markForCheck();
      return;
    }

    if (!this.report || !this.employee) return;

    this.employeeTimeLogs = this.report.timeLogs[0] ?? null;
    this.employeeBooked = this.report.bookedhours[0] ?? null;
    this.employeeSchedule = this.report.userSchedules[0] ?? null;

    this.weekDates = this.deriveWeekDates(
      this.employeeTimeLogs,
      this.employeeBooked,
      this.employeeSchedule,
    );

    this.rows = this.buildWonRowsFromBooked();
    this.recomputeAllTotals();
    this.saveBypassEmployeeApproval = false;
    this.captureSnapshot();

    this.initPunchDaySelection();
    this.rebuildPunchesForSelectedDay();

    this.showToast('info', 'Changes discarded.');
    this.cd.markForCheck();
  }

  async deleteWonRow(row: AdminRow): Promise<void> {
    if (this.blockIfPayrollSynced('Deleting booked hours')) {
      return;
    }

    if (row.isNew) {
      this.rows = this.rows.filter((item) => item.rowId !== row.rowId);
      this.recomputeAllTotals();
      this.markDirty();
      this.cd.markForCheck();
      return;
    }

    const confirmed = window.confirm(
      'Delete this WON row?\n\nThis will remove all booked hours for this WON + Pay Code for the entire week when you click Save.',
    );
    if (!confirmed) return;

    row.pendingDelete = true;
    row.dailyBooked = Array(7).fill(0);
    row.rowTotal = 0;

    this.recomputeAllTotals();
    this.markDirty();
    this.cd.markForCheck();
  }

  async saveWeek(): Promise<void> {
    if (this.blockIfPayrollSynced('Saving booked hours')) {
      this.isDirty = false;
      this.captureSnapshot();
      return;
    }

    if (!this.employee || !this.weekDates.length) return;

    this.isSaving = true;
    this.errorMessage = null;
    this.refreshView();

    try {
      const tasks: Array<() => Observable<unknown>> = [];

      for (const row of this.rows) {
        const isNew = !!row.isNew;

        if (isNew && !this.hasAnyBookedHours(row)) {
          continue;
        }

        if (isNew) {
          const hasWon = !!row.workOrderNumberId;
          const payCodeId = Number(row.payCodeId);
          const hasPayCode = Number.isFinite(payCodeId) && payCodeId > 0;

          if (!hasWon || !hasPayCode) {
            continue;
          }
        }

        for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
          const bookedDate = this.weekDates[dayIndex];
          const existingItems = (row.dailyBookedItems[dayIndex] ?? []).filter(
            (item) => !!item?.id,
          );

          const existingTotal = this.round2(this.sumBookedHourItems(existingItems));
          const requestedTotal = row.pendingDelete
            ? 0
            : this.round2(Number(row.dailyBooked[dayIndex] ?? 0));

          if (requestedTotal < 0) {
            throw new Error(
              `Hours cannot be negative for ${this.getDayLabel(dayIndex)} ${bookedDate}.`,
            );
          }

          if (this.isMatch(requestedTotal, existingTotal)) {
            continue;
          }

          if (existingTotal <= 0 || !existingItems.length || isNew) {
            if (requestedTotal > 0) {
              tasks.push(() => this.createBookedHoursForRow(row, dayIndex, requestedTotal));
            }

            continue;
          }

          this.queueBookedHourApprovalChangeTasks(tasks, existingItems, requestedTotal);
        }
      }

      if (!tasks.length) {
        this.isDirty = false;
        this.captureSnapshot();
        this.showToast('info', 'No changes to save.');
        return;
      }

      await this.runTasks(tasks, 8);

      const usedBypassEmployeeApproval = this.saveBypassEmployeeApproval;

      this.rows = this.rows.filter((row) => !row.pendingDelete);
      this.saveBypassEmployeeApproval = false;

      await this.refreshWeeklyReportOnly(true);
      this.showToast(
        'success',
        usedBypassEmployeeApproval
          ? 'Timesheet changed and approved.'
          : 'Timesheet changes saved.',
      );
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Failed to save timesheet.';

      this.errorMessage = message;
      this.showToast('error', message);
    } finally {
      this.isSaving = false;
      this.refreshView();
    }
  }

  private queueBookedHourApprovalChangeTasks(
    tasks: Array<() => Observable<unknown>>,
    existingItems: WeeklyReportBookedHourItem[],
    requestedTotal: number,
  ): void {
    const changes = this.buildExactBookedHourChanges(existingItems, requestedTotal);

    for (const change of changes) {
      tasks.push(() =>
        this.saveBypassEmployeeApproval
          ? this.svc.approveBookedHourWithChange(change.bookedHourId, change.newHours)
          : this.svc.requestBookedHourChange(change.bookedHourId, change.newHours),
      );
    }
  }

  private buildExactBookedHourChanges(
    existingItems: WeeklyReportBookedHourItem[],
    requestedTotal: number,
  ): Array<{ bookedHourId: number; newHours: number }> {
    const editableItems = (existingItems ?? [])
      .filter((item) => !!item?.id)
      .map((item) => ({
        bookedHourId: item.id,
        currentHours: this.round2(Number(item.hours) || 0),
      }));

    if (!editableItems.length) {
      return [];
    }

    const cleanRequestedTotal = this.round2(Math.max(0, Number(requestedTotal) || 0));
    const existingTotal = this.round2(
      editableItems.reduce((sum, item) => sum + item.currentHours, 0),
    );

    if (this.isMatch(cleanRequestedTotal, existingTotal)) {
      return [];
    }

    const nextHoursById = new Map<number, number>(
      editableItems.map((item) => [item.bookedHourId, item.currentHours]),
    );

    const difference = this.round2(cleanRequestedTotal - existingTotal);

    if (difference > 0) {
      const primaryItem = editableItems[0];
      nextHoursById.set(
        primaryItem.bookedHourId,
        this.round2(primaryItem.currentHours + difference),
      );
    } else {
      let remainingReduction = this.round2(Math.abs(difference));

      for (const item of editableItems) {
        if (remainingReduction <= 0) {
          break;
        }

        const currentHours = nextHoursById.get(item.bookedHourId) ?? 0;
        const reduction = Math.min(currentHours, remainingReduction);
        const nextHours = this.round2(currentHours - reduction);

        nextHoursById.set(item.bookedHourId, nextHours);
        remainingReduction = this.round2(remainingReduction - reduction);
      }

      if (remainingReduction > 0.01) {
        throw new Error('Hours cannot be reduced below zero.');
      }
    }

    const changes = editableItems
      .map((item) => ({
        bookedHourId: item.bookedHourId,
        newHours: this.round2(nextHoursById.get(item.bookedHourId) ?? 0),
        currentHours: item.currentHours,
      }))
      .filter((item) => !this.isMatch(item.currentHours, item.newHours));

    const projectedTotal = this.round2(
      editableItems.reduce(
        (sum, item) => sum + (nextHoursById.get(item.bookedHourId) ?? 0),
        0,
      ),
    );

    if (!this.isMatch(projectedTotal, cleanRequestedTotal)) {
      throw new Error(
        `Unable to calculate exact booked hours change. Expected ${cleanRequestedTotal.toFixed(
          2,
        )}, calculated ${projectedTotal.toFixed(2)}.`,
      );
    }

    return changes.map(({ bookedHourId, newHours }) => ({ bookedHourId, newHours }));
  }

  get hasSaveBypassEmployeeApprovalOption(): boolean {
    return !this.isPayrollSynced && this.hasExistingBookedHourChanges();
  }

  private hasExistingBookedHourChanges(): boolean {
    for (const row of this.rows) {
      for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
        const existingItems = row.dailyBookedItems?.[dayIndex] ?? [];
        const existingTotal = this.round2(this.sumBookedHourItems(existingItems));
        const requestedTotal = row.pendingDelete
          ? 0
          : this.round2(Number(row.dailyBooked?.[dayIndex] ?? 0));

        if (existingTotal > 0 && !this.isMatch(existingTotal, requestedTotal)) {
          return true;
        }
      }
    }

    return false;
  }

  private hasAnyBookedHours(row: AdminWonRowVm): boolean {
    return (row.dailyBooked ?? []).some((value) => Number(value ?? 0) > 0);
  }

  private isSupervisorApprovedBookedHour(item: WeeklyReportBookedHourItem | null | undefined): boolean {
    return !!item?.supervisorApprovedDate;
  }

  private sumBookedHourItems(items: WeeklyReportBookedHourItem[]): number {
    return this.round2(items.reduce((sum, item) => sum + (Number(item.hours) || 0), 0));
  }

  private createBookedHoursForRow(
    row: AdminWonRowVm,
    dayIndex: number,
    hours: number,
  ): Observable<unknown> {
    if (this.isPayrollSynced) {
      throw new Error('Cannot create booked hours because payroll sync has already been run for this week.');
    }

    const employeeId = this.employee?.id;
    const bookedDate = this.weekDates[dayIndex];
    const workOrderNumberId = Number(row.workOrderNumberId);
    const payCodeId = Number(row.payCodeId);
    const cleanHours = this.round2(Number(hours) || 0);

    if (
      !employeeId ||
      !bookedDate ||
      !Number.isFinite(workOrderNumberId) ||
      workOrderNumberId <= 0 ||
      !Number.isFinite(payCodeId) ||
      payCodeId <= 0 ||
      cleanHours <= 0
    ) {
      throw new Error(
        `Cannot create booked hours for ${this.getDayLabel(dayIndex)}. Missing employee, WON, pay code, date, or hours.`,
      );
    }

    return this.svc.createBookedHours({
      employeeId,
      hours: cleanHours,
      bookedDate,
      workOrderNumberId,
      payCodeId,
    });
  }


  private async runTasks(tasks: Array<() => Observable<unknown>>, concurrency = 8): Promise<void> {
    await firstValueFrom(
      from(tasks).pipe(
        mergeMap((fn) => fn(), concurrency),
        toArray(),
      ),
    );
  }

  private computeClocked(): void {
    this.dailyClocked = Array(7).fill(0);

    for (let index = 0; index < 7; index += 1) {
      const date = this.weekDates[index];

      const matchingDays =
        this.employeeTimeLogs?.logs?.filter((day) => day.date === date) ?? [];

      const mergedDayLog: WeeklyReportDayLog = {
        date,
        timeLogs: matchingDays.flatMap((day) => day.timeLogs ?? []),
        invalidUserClockLogs: Array.from(
          new Map(
            matchingDays
              .flatMap((day) => day.invalidUserClockLogs ?? [])
              .map((log) => [log.id, log]),
          ).values(),
        ),
      };

      this.dailyClocked[index] = this.svc.computeTotalClockedHoursForDay(mergedDayLog);
    }

    this.weeklyClockedTotal = this.sum(this.dailyClocked);
  }

  private computePunchTooltips(): void {
    this.formattedPunches = Array(7).fill('');

    for (let index = 0; index < 7; index += 1) {
      const date = this.weekDates[index];

      const matchingDays =
        this.employeeTimeLogs?.logs?.filter((day) => day.date === date) ?? [];

      const tooltip = matchingDays
        .flatMap((day) => day.timeLogs ?? [])
        .map((log) => {
          const lines: string[] = [];

          if (log.clockIn?.logTime) {
            lines.push(`In: ${this.formatTime(log.clockIn.logTime)}`);
          }

          if (log.clockOut?.logTime) {
            lines.push(`Out: ${this.formatTime(log.clockOut.logTime)}`);
          } else {
            lines.push('Out: Missing punch');
          }

          return lines.join('\n');
        })
        .join('\n\n');

      this.formattedPunches[index] = tooltip;
    }
  }

  private computeScheduled(): void {
    this.dailyScheduled = Array(7).fill(null);

    const schedulesByDate = new Map<string, WeeklyReportAssignedSchedule>();

    for (const assigned of this.employeeSchedule?.schedules ?? []) {
      const date = assigned.assignedDate;
      const schedule = assigned.schedule;

      if (!date || !schedule?.localStartTime || !schedule?.localEndTime) {
        continue;
      }

      schedulesByDate.set(date, assigned);
    }

    for (let index = 0; index < Math.min(7, this.weekDates.length); index += 1) {
      const date = this.weekDates[index];
      this.dailyScheduled[index] = schedulesByDate.get(date) ?? null;
    }

    this.weeklyScheduledTotal = this.dailyScheduled.reduce((sum, assigned) => {
      if (!assigned) return sum;

      return sum + this.computeDurationHours(assigned);
    }, 0);
  }

  getDurationHours(schedule: WeeklyReportAssignedSchedule): {
    totalHours: number;
    breakHours: number;
  } {
    if (!schedule?.schedule?.localStartTime || !schedule?.schedule?.localEndTime) {
      return { totalHours: 0, breakHours: 0 };
    }

    const [startHours, startMinutes] = schedule.schedule.localStartTime.split(':');
    const [endHours, endMinutes] = schedule.schedule.localEndTime.split(':');

    const startHoursNum = startHours ? Number(startHours) : 0;
    const startMinutesNum = startMinutes ? Number(startMinutes) : 0;
    const endHoursNum = endHours ? Number(endHours) : 0;
    const endMinutesNum = endMinutes ? Number(endMinutes) : 0;
    if (
      startHoursNum < 0 ||
      startHoursNum > 23 ||
      startMinutesNum < 0 ||
      startMinutesNum > 59 ||
      endHoursNum < 0 ||
      endHoursNum > 23 ||
      endMinutesNum < 0 ||
      endMinutesNum > 59
    ) {
      return { totalHours: 0, breakHours: 0 };
    }

    let durationMinutes = endHoursNum * 60 + endMinutesNum - (startHoursNum * 60 + startMinutesNum);

    if (schedule.schedule.isOverNight) {
      durationMinutes += 24 * 60;
    }

    const breakMinutesTotal = this.getBreakTimeInMinutes(schedule);
    const breakHoursNum = Math.floor(breakMinutesTotal / 60);
    const breakMinutesNum = breakMinutesTotal % 60;

    if (
      schedule.schedule.breakThresholdHours &&
      durationMinutes < schedule.schedule.breakThresholdHours * 60
    ) {
      return {
        totalHours: Math.round((durationMinutes / 60) * 100) / 100,
        breakHours: 0,
      };
    }

    const netMinutes = durationMinutes - this.getBreakTimeInMinutes(schedule);

    return {
      totalHours: Math.round((netMinutes / 60) * 100) / 100,
      breakHours: Math.round((breakHoursNum + breakMinutesNum / 60) * 100) / 100,
    };
  }

  getBreakTimeInMinutes(schedule: WeeklyReportAssignedSchedule): number {
    const breakTime = String(schedule?.schedule?.breakTime ?? '00:00');
    const [breakHours, breakMinutes] = breakTime.split(':');
    let breakHoursNum = breakHours ? Number(breakHours) : 0;
    const breakMinutesNum = breakMinutes ? Number(breakMinutes) : 0;

    if (!Number.isFinite(breakHoursNum) || !Number.isFinite(breakMinutesNum)) {
      return 0;
    }

    if (breakHoursNum === 12) {
      breakHoursNum = 0;
    }

    return Math.max(0, breakHoursNum * 60 + breakMinutesNum);
  }

  getScheduledTimeRange(assigned: WeeklyReportAssignedSchedule | null): string {
    if (!assigned) return '—';
    const start = assigned.schedule.localStartTime;
    const end = assigned.schedule.localEndTime;
    if (!start || !end) return '—';
    return `${start} - ${end}`;
  }

  private getBookedEntriesForDay(day: {
    bookedHours?: WeeklyReportBookedHourItem[] | null;
    bookedHour?: WeeklyReportBookedHourItem[] | null;
  } | null | undefined): WeeklyReportBookedHourItem[] {
    return day?.bookedHours ?? day?.bookedHour ?? [];
  }

  private buildWonRowsFromBooked(): AdminRow[] {
    const rowsByLane = new Map<string, AdminRow>();
    const bookedByDate = new Map<string, WeeklyReportBookedHourItem[]>();

    for (const day of this.employeeBooked?.bookedHours ?? []) {
      if (day?.date) {
        bookedByDate.set(day.date, this.getBookedEntriesForDay(day));
      }
    }

    for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
      const bookedDate = this.weekDates[dayIndex];
      const items = bookedByDate.get(bookedDate) ?? [];
      const itemsByCombo = new Map<string, WeeklyReportBookedHourItem[]>();

      for (const item of items) {
        const hours = this.round2(Number(item?.hours) || 0);
        const won = item?.workOrderNumber;

        if (!item?.id || hours <= 0 || !won?.id) {
          continue;
        }

        const payCodeId = item.payCode?.id ?? null;
        const comboKey = this.getBookedHourComboKey(won.id, payCodeId);
        const comboItems = itemsByCombo.get(comboKey) ?? [];

        comboItems.push(item);
        itemsByCombo.set(comboKey, comboItems);
      }

      for (const [comboKey, comboItems] of itemsByCombo.entries()) {
        const sortedItems = [...comboItems].sort((a, b) => Number(a.id) - Number(b.id));

        sortedItems.forEach((item, laneIndex) => {
          const won = item.workOrderNumber;

          if (!won?.id) {
            return;
          }

          const payCodeId = item.payCode?.id ?? null;
          const payCodeName = item.payCode?.name ?? '-';
          const laneKey = `${comboKey}:lane:${laneIndex}`;

          if (!rowsByLane.has(laneKey)) {
            rowsByLane.set(laneKey, {
              rowId: laneKey,
              workOrderNumberId: won.id,
              workOrderNumberLabel: won.won ?? '',
              payCodeId,
              payCodeName,
              dailyBooked: Array(7).fill(0),
              dailyBookedItems: Array.from(
                { length: 7 },
                () => [] as WeeklyReportBookedHourItem[],
              ),
              rowTotal: 0,
              isNew: false,
              pendingDelete: false,
            });
          }

          const row = rowsByLane.get(laneKey)!;
          const hours = this.round2(Number(item.hours) || 0);

          row.dailyBooked[dayIndex] = hours;
          row.dailyBookedItems[dayIndex] = [item];
          row.rowTotal = this.sum(row.dailyBooked);
        });
      }
    }

    return Array.from(rowsByLane.values()).sort((a, b) => {
      const wonCompare = String(a.workOrderNumberLabel ?? '').localeCompare(
        String(b.workOrderNumberLabel ?? ''),
        undefined,
        { numeric: true, sensitivity: 'base' },
      );

      if (wonCompare !== 0) {
        return wonCompare;
      }

      const payCodeCompare = String(a.payCodeName ?? '').localeCompare(
        String(b.payCodeName ?? ''),
        undefined,
        { numeric: true, sensitivity: 'base' },
      );

      if (payCodeCompare !== 0) {
        return payCodeCompare;
      }

      const aLane = this.getRowLaneIndex(a);
      const bLane = this.getRowLaneIndex(b);

      if (aLane !== bLane) {
        return aLane - bLane;
      }

      const aFirstDay = this.getFirstBookedDayIndex(a);
      const bFirstDay = this.getFirstBookedDayIndex(b);

      if (aFirstDay !== bFirstDay) {
        return aFirstDay - bFirstDay;
      }

      return this.getFirstBookedHourId(a) - this.getFirstBookedHourId(b);
    });
  }

  private getBookedHourComboKey(workOrderNumberId: number, payCodeId: number | null): string {
    return `${workOrderNumberId}:${payCodeId ?? 0}`;
  }

  private getRowLaneIndex(row: AdminWonRowVm): number {
    const match = String(row.rowId ?? '').match(/:lane:(\d+)$/);
    return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
  }

  private getFirstBookedDayIndex(row: AdminWonRowVm): number {
    const index = (row.dailyBooked ?? []).findIndex((hours) => Number(hours ?? 0) > 0);
    return index >= 0 ? index : Number.MAX_SAFE_INTEGER;
  }

  private getFirstBookedHourId(row: AdminWonRowVm): number {
    const bookedHour = (row.dailyBookedItems ?? [])
      .flat()
      .find((item) => !!item?.id);

    return Number(bookedHour?.id ?? Number.MAX_SAFE_INTEGER);
  }

  private recomputeAllTotals(): void {
    this.dailyBookedTotals = Array(7).fill(0);

    for (const row of this.rows) {
      for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
        this.dailyBookedTotals[dayIndex] += Number(row.dailyBooked[dayIndex] ?? 0);
      }
    }

    this.weeklyBookedTotal = this.sum(this.dailyBookedTotals);
  }

  private captureSnapshot(): void {
    this.initialRowsSnapshot = this.rows.map((row) => ({
      rowId: row.rowId,
      workOrderNumberId: row.workOrderNumberId,
      workOrderNumberLabel: row.workOrderNumberLabel ?? '',
      payCodeId: row.payCodeId ?? null,
      payCodeName: row.payCodeName ?? '',
      dailyBooked: [...(row.dailyBooked ?? [])],
      dailyBookedItems: (row.dailyBookedItems ?? []).map((cell) => [...cell]),
      rowTotal: row.rowTotal,
      isNew: !!row.isNew,
      pendingDelete: !!row.pendingDelete,
    }));

    this.initialSnapshotJson = JSON.stringify(
      this.rows.map((row) => ({
        workOrderNumberId: row.workOrderNumberId,
        payCodeId: row.payCodeId ?? null,
        dailyBooked: row.dailyBooked,
        isNew: !!row.isNew,
        pendingDelete: !!row.pendingDelete,
      })),
    );

    this.isDirty = false;
    this.cd.markForCheck();
  }

  private markDirty(): void {
    if (this.isPayrollSynced) {
      this.isDirty = false;
      return;
    }

    const currentSnapshot = JSON.stringify(
      this.rows.map((row) => ({
        workOrderNumberId: row.workOrderNumberId,
        payCodeId: row.payCodeId ?? null,
        dailyBooked: row.dailyBooked,
        isNew: !!row.isNew,
        pendingDelete: !!row.pendingDelete,
      })),
    );

    this.isDirty = currentSnapshot !== this.initialSnapshotJson;

    if (!this.isDirty) {
      this.saveBypassEmployeeApproval = false;
    }
  }

  getDayLabel(dayIndex: number): string {
    return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][dayIndex] ?? '';
  }

  isMatch(a: number | null | undefined, b: number | null | undefined, tolerance = 0.01): boolean {
    return Math.abs(Number(a ?? 0) - Number(b ?? 0)) <= tolerance;
  }

  getPunchUpdatedByLabel(punch: PunchEventVm | null | undefined): string {
    const user = punch?.updatedBy;
    if (!user) return '—';

    const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    return fullName || user.employeeNumber || '—';
  }

  getPunchUpdatedAtLabel(punch: PunchEventVm | null | undefined): string {
    if (!punch?.updatedAtUtc) return '—';

    return dayjs
      .utc(punch.updatedAtUtc)
      .tz(punch.timeZoneId || dayjs.tz.guess())
      .format('MMM D, YYYY h:mm A');
  }

  hasPunchAudit(punch: PunchEventVm | null | undefined): boolean {
    return !!punch?.updatedBy || !!punch?.updatedAtUtc;
  }

  canInvalidatePunch(punch: PunchEventVm | null | undefined): boolean {
    if (this.isPayrollSynced || !punch || punch.isInvalid) return false;
    return this.isPunchEventEditable(punch);
  }

  getPunchRowTitle(punch: PunchEventVm): string | null {
    if (!punch.isInvalid) return null;

    const by = this.getPunchUpdatedByLabel(punch);
    const at = this.getPunchUpdatedAtLabel(punch);

    if (by === '—' && at === '—') {
      return 'Invalidated punch';
    }

    return `Invalidated${by !== '—' ? ` by ${by}` : ''}${at !== '—' ? ` on ${at}` : ''}`;
  }

  private sum(values: number[]): number {
    return (values ?? []).reduce((sum, value) => sum + (Number(value) || 0), 0);
  }

  private formatTime(value?: string | null): string {
    if (!value) return 'N/A';
    return dayjs.utc(value).local().format('h:mm A');
  }

  private computeDurationHours(schedule: WeeklyReportAssignedSchedule): number {
    if (!schedule?.schedule?.localStartTime || !schedule?.schedule?.localEndTime) {
      return 0;
    }
    const start = schedule.schedule.localStartTime;
    const end = schedule.schedule.localEndTime;
    const isOverNight = schedule.schedule.isOverNight;

    const [startHours, startMinutes] = start.split(':').map(Number);
    const [endHours, endMinutes] = end.split(':').map(Number);

    const startTotalMinutes = startHours * 60 + startMinutes;
    const endTotalMinutes = endHours * 60 + endMinutes;

    let diff = endTotalMinutes - startTotalMinutes;
    if (isOverNight || diff < 0) {
      diff += 1440;
    }

    const breakMinutes = this.getBreakTimeInMinutes(schedule);
    if (diff >= (schedule?.schedule?.breakThresholdHours ?? 0) * 60) {
      diff -= breakMinutes;
    }

    return diff / 60;
  }

  private round2(value: number): number {
    return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
  }

  private isValidYyyyww(value: string): boolean {
    return /^\d{6}$/.test(String(value ?? '').trim());
  }

  private addWeeks(yyyyww: string, deltaWeeks: number): string {
    const monday = this.isoWeekToMonday(yyyyww);
    monday.setUTCDate(monday.getUTCDate() + deltaWeeks * 7);
    return this.dateToIsoWeek(monday);
  }

  private isoWeekToMonday(yyyyww: string): Date {
    const { year, week } = this.parseYyyyWw(yyyyww);

    const jan4 = new Date(Date.UTC(year, 0, 4));
    const jan4Day = jan4.getUTCDay() || 7;

    const week1Monday = new Date(jan4);
    week1Monday.setUTCDate(jan4.getUTCDate() - (jan4Day - 1));

    const monday = new Date(week1Monday);
    monday.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);

    return monday;
  }

  private dateToIsoWeek(date: Date): string {
    const utcDate = new Date(
      Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
    );

    const day = utcDate.getUTCDay() || 7;
    utcDate.setUTCDate(utcDate.getUTCDate() + (4 - day));

    const year = utcDate.getUTCFullYear();

    const jan4 = new Date(Date.UTC(year, 0, 4));
    const jan4Day = jan4.getUTCDay() || 7;

    const week1Thursday = new Date(jan4);
    week1Thursday.setUTCDate(jan4.getUTCDate() + (4 - jan4Day));

    const diffDays = Math.round((utcDate.getTime() - week1Thursday.getTime()) / 86400000);
    const week = 1 + Math.floor(diffDays / 7);

    return `${year}${String(week).padStart(2, '0')}`;
  }

  private parseYyyyWw(yyyyww: string): { year: number; week: number } {
    const value = String(yyyyww).trim();

    if (!/^\d{6}$/.test(value)) {
      throw new Error(`Invalid YYYYWW: ${yyyyww}`);
    }

    const year = Number(value.slice(0, 4));
    const week = Number(value.slice(4, 6));

    if (!Number.isFinite(year) || !Number.isFinite(week) || week < 1 || week > 53) {
      throw new Error(`Invalid ISO week: ${yyyyww}`);
    }

    return { year, week };
  }

  onSaveBypassEmployeeApprovalChange(value: boolean): void {
    this.saveBypassEmployeeApproval = !!value;
    this.cd.markForCheck();
  }

  blockLockedInteraction(event?: Event, actionLabel = 'This action'): boolean {
    if (!this.isPayrollSynced) {
      return false;
    }

    event?.preventDefault();
    event?.stopPropagation();

    const target = event?.target as HTMLElement | null;
    if (target && typeof target.blur === 'function') {
      target.blur();
    }

    this.blockIfPayrollSynced(actionLabel);
    return true;
  }

  onHourFocus(event: FocusEvent): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) return;

    if (this.isPayrollSynced) {
      input.blur();
      this.blockIfPayrollSynced('Editing booked hours');
      return;
    }

    if (input.value === '0' || input.value === '0.0' || input.value === '0.00') {
      input.value = '';
    }
  }

  onHourBlur(row: AdminWonRowVm, dayIndex: number, event: FocusEvent): void {
    if (this.blockIfPayrollSynced('Editing booked hours')) {
      return;
    }

    const input = event.target as HTMLInputElement | null;
    if (!input) return;

    const raw = (input.value ?? '').trim();

    if (!raw) {
      row.dailyBooked[dayIndex] = 0;
      row.rowTotal = this.sum(row.dailyBooked);
      this.recomputeAllTotals();
      this.markDirty();
      this.cd.markForCheck();
      return;
    }

    const numericValue = Number(raw);
    const value = Number.isFinite(numericValue) ? Math.max(0, numericValue) : 0;

    row.dailyBooked[dayIndex] = value;
    row.rowTotal = this.sum(row.dailyBooked);

    this.recomputeAllTotals();
    this.markDirty();
    this.cd.markForCheck();
  }

  isOver(a: number | null | undefined, b: number | null | undefined, tolerance = 0.01): boolean {
    return Number(a ?? 0) - Number(b ?? 0) > tolerance;
  }

  isUnder(a: number | null | undefined, b: number | null | undefined, tolerance = 0.01): boolean {
    return Number(b ?? 0) - Number(a ?? 0) > tolerance;
  }

  openApproveDay(dayIndex: number): void {
    if (this.blockIfPayrollSynced('Approving booked hours')) {
      return;
    }

    this.openApprovalModal('day', 'approve', dayIndex);
  }

  openChangeDay(dayIndex: number): void {
    if (this.blockIfPayrollSynced('Changing booked hours')) {
      return;
    }

    this.openApprovalModal('day', 'change', dayIndex);
  }

  openApproveWeek(): void {
    if (this.blockIfPayrollSynced('Approving booked hours')) {
      return;
    }

    this.openApprovalModal('week', 'approve', null);
  }

  openChangeWeek(): void {
    if (this.blockIfPayrollSynced('Changing booked hours')) {
      return;
    }

    this.openApprovalModal('week', 'change', null);
  }

  hasApprovalItemsForDay(dayIndex: number): boolean {
    return !this.isPayrollSynced && this.getUnapprovedBookedHourItemsForDay(dayIndex).length > 0;
  }

  getApprovalModalTitle(): string {
    if (this.approvalAction === 'approve') {
      return this.approvalScope === 'week' ? 'Approve Full Week' : 'Approve Day';
    }

    return this.approvalScope === 'week' ? 'Request Changes for Full Week' : 'Request Changes for Day';
  }

  getApprovalModalSubtitle(): string {
    const count = this.approvalRows.length;
    const label = count === 1 ? 'booked hour' : 'booked hours';

    if (this.approvalAction === 'approve') {
      return `You are approving ${count} ${label}. Approved booked hours will be locked from employee edits.`;
    }

    return 'Edit only the rows that need to change. Unchanged rows will not be submitted.';
  }

  getApprovalSubmitLabel(): string {
    if (this.isApprovalBusy) {
      return 'Submitting...';
    }

    if (this.approvalAction === 'approve') {
      return 'Approve Hours';
    }

    return this.approvalBypassEmployeeApproval ? 'Change and Approve' : 'Request Changes';
  }

  private openApprovalModal(
    scope: ApprovalScope,
    action: ApprovalAction,
    dayIndex: number | null,
  ): void {
    if (this.blockIfPayrollSynced('Approval actions')) {
      return;
    }

    this.approvalScope = scope;
    this.approvalAction = action;
    this.approvalDayIndex = dayIndex;
    this.approvalBypassEmployeeApproval = false;
    this.approvalError = null;
    this.approvalRows =
      scope === 'day' && dayIndex !== null
        ? this.buildApprovalRowsForDay(dayIndex)
        : this.buildApprovalRowsForWeek();

    if (!this.approvalRows.length) {
      this.showToast('info', 'No booked hours found for approval.');
      return;
    }

    this.approvalModalOpen = true;
    this.cd.markForCheck();
  }

  closeApprovalModal(): void {
    if (this.isApprovalBusy) {
      return;
    }

    this.approvalModalOpen = false;
    this.approvalRows = [];
    this.approvalError = null;
    this.approvalDayIndex = null;
    this.approvalBypassEmployeeApproval = false;
    this.cd.markForCheck();
  }

  async submitApprovalAction(): Promise<void> {
    if (this.blockIfPayrollSynced('Approval actions')) {
      this.closeApprovalModal();
      return;
    }

    this.approvalError = null;

    if (!this.approvalRows.length) {
      this.approvalError = 'No booked hours selected.';
      this.cd.markForCheck();
      return;
    }

    const tasks: Array<() => Observable<unknown>> = [];

    if (this.approvalAction === 'approve') {
      for (const row of this.approvalRows) {
        tasks.push(() => this.svc.approveBookedHour(row.bookedHourId));
      }
    } else {
      const changedRows = this.approvalRows.filter(
        (row) => !this.isMatch(row.currentHours, row.newHours),
      );

      if (!changedRows.length) {
        this.approvalError = 'Change at least one hours value before submitting.';
        this.cd.markForCheck();
        return;
      }

      for (const row of changedRows) {
        const newHours = Number(row.newHours);

        if (!Number.isFinite(newHours) || newHours < 0) {
          this.approvalError = 'All changed hours must be valid numbers.';
          this.cd.markForCheck();
          return;
        }

        tasks.push(() =>
          this.approvalBypassEmployeeApproval
            ? this.svc.approveBookedHourWithChange(row.bookedHourId, this.round2(newHours))
            : this.svc.requestBookedHourChange(row.bookedHourId, this.round2(newHours)),
        );
      }
    }

    this.isApprovalBusy = true;
    this.cd.markForCheck();

    try {
      await this.runTasks(tasks, 8);
      await this.refreshWeeklyReportOnly(true);

      this.showToast(
        'success',
        this.approvalAction === 'approve'
          ? 'Booked hours approved.'
          : this.approvalBypassEmployeeApproval
            ? 'Booked hours changed and approved.'
            : 'Booked hour changes requested.',
      );

      this.approvalModalOpen = false;
      this.approvalRows = [];
      this.approvalDayIndex = null;
      this.approvalBypassEmployeeApproval = false;
    } catch {
      this.approvalError = 'Failed to submit approval action.';
      this.showToast('error', 'Failed to submit approval action.');
    } finally {
      this.isApprovalBusy = false;
      this.refreshView();
    }
  }

  private getBookedHourItemsForDay(dayIndex: number): WeeklyReportBookedHourItem[] {
    return this.rows
      .flatMap((row) => row.dailyBookedItems?.[dayIndex] ?? [])
      .filter((item) => !!item?.id && Number(item.hours ?? 0) > 0);
  }

  private getUnapprovedBookedHourItemsForDay(dayIndex: number): WeeklyReportBookedHourItem[] {
    return this.getBookedHourItemsForDay(dayIndex).filter(
      (item) => !this.isSupervisorApprovedBookedHour(item),
    );
  }

  private getAllBookedHourItemsForWeek(): WeeklyReportBookedHourItem[] {
    return [0, 1, 2, 3, 4, 5, 6].flatMap((dayIndex) =>
      this.getBookedHourItemsForDay(dayIndex),
    );
  }

  private getAllUnapprovedBookedHourItemsForWeek(): WeeklyReportBookedHourItem[] {
    return [0, 1, 2, 3, 4, 5, 6].flatMap((dayIndex) =>
      this.getUnapprovedBookedHourItemsForDay(dayIndex),
    );
  }

  private getApprovedBySupervisorName(item: WeeklyReportBookedHourItem): string {
    const user = item.approvedBySupervisor;

    if (!user) {
      return 'Not approved';
    }

    const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();

    return fullName || user.employeeNumber || 'Approved';
  }

  private buildApprovalRowsForDay(dayIndex: number): ApprovalItemVm[] {
    return this.rows.flatMap((row) =>
      (row.dailyBookedItems?.[dayIndex] ?? [])
        .filter(
          (item) =>
            !!item?.id &&
            Number(item.hours ?? 0) > 0 &&
            !this.isSupervisorApprovedBookedHour(item),
        )
        .map((item) => ({
          bookedHourId: item.id,
          dayIndex,
          date: item.bookedDate || this.weekDates[dayIndex] || '',
          dayLabel: this.getDayLabel(dayIndex),
          won: item.workOrderNumber?.won ?? row.workOrderNumberLabel ?? '—',
          payCode: item.payCode?.name ?? row.payCodeName ?? '—',
          currentHours: this.round2(Number(item.hours) || 0),
          newHours: this.round2(Number(item.hours) || 0),
          supervisorApprovedDate: item.supervisorApprovedDate ?? null,
          approvedBySupervisorName: this.getApprovedBySupervisorName(item),
        })),
    );
  }

  private buildApprovalRowsForWeek(): ApprovalItemVm[] {
    return [0, 1, 2, 3, 4, 5, 6].flatMap((dayIndex) => this.buildApprovalRowsForDay(dayIndex));
  }

}